import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import SettlementLetterModal from './SettlementLetterModal';

/**
 * LettersTab
 *
 * Case-level letter generation. Sits between Insurance (Med Pay) and
 * MayLogic in the project tab strip.
 *
 * Three generators live here:
 *   - Overpayment        -> client, encloses a refund check
 *   - Additional monies  -> client, forwards additional settlement funds
 *   - Lien payment       -> provider, works with NO negotiation on file
 *
 * The made-whole waiver is deliberately NOT here — it's generated per
 * lienholder, so its button lives on each Liens tab row. A pointer card
 * explains that so nobody hunts for it.
 *
 * Every form pre-fills from /letter-context (client address from Filevine,
 * case financials from the overlay, a draft narrative from the provider
 * list) and every field stays editable before generating.
 */
function LettersTab({ projectId, clientName }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // 'overpayment' | 'additional' | 'lienpayment'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/projects/${projectId}/letter-context`,
        { withCredentials: true }
      );
      setCtx(res.data);
    } catch (e) {
      setError('Could not load case details: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadContext(); }, [loadContext]);

  /** POST a letter payload and trigger the .docx download. */
  const generate = async (endpoint, payload, filename) => {
    setBusy(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/${endpoint}`,
        payload,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setOpenForm(null);
    } catch (e) {
      // Blob error responses need decoding before we can show the detail.
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep msg */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      setError('Letter generation failed: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  const name = ctx?.client_name || clientName || '';
  const addressText = (ctx?.client_address_lines || []).join('\n');

  if (loading) {
    return <div style={styles.loading}>Loading case details…</div>;
  }

  return (
    <div style={styles.wrap}>
      <p style={styles.intro}>
        Generate letters on firm letterhead with Robert May's signature. Each opens
        a short form pre-filled from the case, then downloads a .docx you can review
        and edit before sending.
      </p>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.grid}>
        <LetterCard
          icon="§"
          iconColor="#A1B808"
          title="Settlement letter"
          desc="To the client — the distribution of their settlement, which they sign. Pre-Lit template only for now."
          onClick={() => setOpenForm('settlement')}
        />
        <LetterCard
          icon="↩"
          iconColor="#534AB7"
          title="Overpayment"
          desc="To the client, enclosing a refund check for an overpayment recovered from a provider."
          onClick={() => setOpenForm('overpayment')}
        />
        <LetterCard
          icon="$"
          iconColor="#1D9E75"
          title="Additional monies"
          desc="To the client, forwarding additional settlement funds that came back after disbursement."
          onClick={() => setOpenForm('additional')}
        />
        <LetterCard
          icon="✎"
          iconColor="#185FA5"
          title="Lien payment"
          desc="To a provider with payment details. Works with no negotiation on file — enter payee and amount manually."
          onClick={() => setOpenForm('lienpayment')}
        />
        <div style={{ ...styles.card, opacity: 0.75 }}>
          <div style={styles.cardHead}>
            <span style={{ ...styles.cardIcon, color: '#993C1D' }}>§</span>
            <span style={styles.cardTitle}>Made-whole waiver</span>
          </div>
          <div style={styles.cardDesc}>
            Lives on the <strong>Liens tab</strong> instead — it's generated per
            lienholder, so the button sits on each lien row.
          </div>
          <button type="button" style={styles.cardBtnDisabled} disabled>
            See Liens tab →
          </button>
        </div>
      </div>

      {openForm === 'settlement' && (
        <SettlementLetterModal
          projectId={projectId}
          clientName={clientName}
          close={() => setOpenForm(null)}
        />
      )}

      {openForm === 'overpayment' && (
        <LetterForm
          title="Overpayment letter"
          subtitle="Sent to the client with a refund check enclosed."
          busy={busy}
          onCancel={() => setOpenForm(null)}
          fields={[
            { key: 'client_name', label: 'Client name', default: name, required: true },
            { key: 'client_title', label: 'Courtesy title', default: '', placeholder: 'Mr. / Ms.' },
            { key: 'client_address', label: 'Mailing address', default: addressText, textarea: true,
              hint: (ctx?.client_address_lines || []).length ? 'Pre-filled from Filevine — edit if wrong.' : 'Not found in Filevine — enter manually.' },
            { key: 'amount', label: 'Check amount ($)', default: '', required: true, decimal: true },
            { key: 'provider_name', label: 'Overpayment was made to', default: '', required: true,
              placeholder: 'e.g. American Ambulance', datalist: (ctx?.providers || []).map(p => p.name) },
            { key: 'date_of_incident', label: 'Date of incident', default: ctx?.date_of_incident || '' },
          ]}
          onSubmit={(v) => generate('overpayment', {
            client_name: v.client_name,
            client_title: v.client_title,
            client_address_lines: (v.client_address || '').split('\n').filter(Boolean),
            amount: parseFloat(v.amount),
            provider_name: v.provider_name,
            date_of_incident: v.date_of_incident || null,
          }, `Overpayment Letter_ ${v.client_name}.docx`)}
        />
      )}

      {openForm === 'additional' && (
        <LetterForm
          title="Additional monies letter"
          subtitle="Sent to the client, forwarding additional settlement funds."
          busy={busy}
          onCancel={() => setOpenForm(null)}
          fields={[
            { key: 'client_name', label: 'Client name', default: name, required: true },
            { key: 'client_title', label: 'Courtesy title', default: '', placeholder: 'Mr. / Ms.' },
            { key: 'client_address', label: 'Mailing address', default: addressText, textarea: true,
              hint: (ctx?.client_address_lines || []).length ? 'Pre-filled from Filevine — edit if wrong.' : 'Not found in Filevine — enter manually.' },
            { key: 'amount', label: 'Check amount ($)', default: '', required: true, decimal: true },
            { key: 'provider_name', label: 'Refund received from', default: '',
              placeholder: 'e.g. Lab Corp of America', datalist: (ctx?.providers || []).map(p => p.name) },
            { key: 'source_description', label: 'Custom explanation (optional)', default: '', textarea: true,
              hint: 'Leave blank to use: "Our office received a refund for overpayment from [provider]."' },
            { key: 'date_of_incident', label: 'Date of incident', default: ctx?.date_of_incident || '' },
          ]}
          onSubmit={(v) => generate('additional-monies', {
            client_name: v.client_name,
            client_title: v.client_title,
            client_address_lines: (v.client_address || '').split('\n').filter(Boolean),
            amount: parseFloat(v.amount),
            provider_name: v.provider_name || null,
            source_description: v.source_description || null,
            date_of_incident: v.date_of_incident || null,
          }, `Additional Monies Letter_ ${v.client_name}.docx`)}
        />
      )}

      {openForm === 'lienpayment' && (
        <LetterForm
          title="Lien payment letter"
          subtitle="Sent to a provider with a payment check. Use for direct-pay liens where no negotiation exists."
          busy={busy}
          onCancel={() => setOpenForm(null)}
          fields={[
            { key: 'payee_name', label: 'Provider / payee name', default: '', required: true,
              datalist: (ctx?.providers || []).map(p => p.name) },
            { key: 'payee_address', label: 'Payee mailing address', default: '', textarea: true, required: true },
            { key: 'client_name', label: 'Client name', default: name, required: true },
            { key: 'client_dob', label: 'Client DOB', default: '' },
            { key: 'amount', label: 'Payment amount ($)', default: '', required: true, decimal: true },
            { key: 'original_lien_amount', label: 'Original lien amount ($) — optional', default: '', decimal: true,
              hint: 'When provided, the letter shows original vs. final as a before/after block.' },
          ]}
          onSubmit={(v) => generate('lien-payment-manual', {
            payee_name: v.payee_name,
            payee_address_lines: (v.payee_address || '').split('\n').filter(Boolean),
            client_name: v.client_name,
            client_dob: v.client_dob || null,
            amount: parseFloat(v.amount),
            original_lien_amount: v.original_lien_amount ? parseFloat(v.original_lien_amount) : null,
          }, `Lien Payment Letter_ ${v.payee_name}.docx`)}
        />
      )}
    </div>
  );
}

function LetterCard({ icon, iconColor, title, desc, onClick }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardHead}>
        <span style={{ ...styles.cardIcon, color: iconColor }}>{icon}</span>
        <span style={styles.cardTitle}>{title}</span>
      </div>
      <div style={styles.cardDesc}>{desc}</div>
      <button type="button" style={styles.cardBtn} onClick={onClick}>Generate</button>
    </div>
  );
}

/**
 * Generic modal form. `fields` is a declarative spec so each letter type
 * only has to describe its inputs, not re-implement the layout.
 */
function LetterForm({ title, subtitle, fields, onSubmit, onCancel, busy }) {
  const [values, setValues] = useState(() => {
    const init = {};
    fields.forEach(f => { init[f.key] = f.default ?? ''; });
    return init;
  });

  const set = (k, v) => setValues(prev => ({ ...prev, [k]: v }));

  const missing = fields.filter(f => f.required && !String(values[f.key] || '').trim());

  return (
    <div style={styles.backdrop} onClick={onCancel}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalTitle}>{title}</div>
        <div style={styles.modalSubtitle}>{subtitle}</div>

        {fields.map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={styles.label}>
              {f.label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}
            </label>
            {f.textarea ? (
              <textarea
                value={values[f.key]}
                onChange={(e) => set(f.key, e.target.value)}
                rows={3}
                style={styles.textarea}
                placeholder={f.placeholder || ''}
              />
            ) : (
              <>
                <input
                  type="text"
                  inputMode={f.decimal ? 'decimal' : undefined}
                  value={values[f.key]}
                  onChange={(e) => {
                    if (f.decimal) {
                      // digits + at most one decimal point
                      const raw = e.target.value;
                      if (!/^[0-9.]*$/.test(raw)) return;
                      if ((raw.match(/\./g) || []).length > 1) return;
                      set(f.key, raw);
                    } else {
                      set(f.key, e.target.value);
                    }
                  }}
                  style={styles.input}
                  placeholder={f.placeholder || ''}
                  list={f.datalist ? `dl-${f.key}` : undefined}
                />
                {f.datalist && (
                  <datalist id={`dl-${f.key}`}>
                    {f.datalist.map((opt, i) => <option key={i} value={opt} />)}
                  </datalist>
                )}
              </>
            )}
            {f.hint && <div style={styles.hint}>{f.hint}</div>}
          </div>
        ))}

        <div style={styles.modalActions}>
          <button type="button" style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            style={{ ...styles.primaryBtn, opacity: (busy || missing.length) ? 0.5 : 1 }}
            disabled={busy || missing.length > 0}
            onClick={() => onSubmit(values)}
            title={missing.length ? `Required: ${missing.map(m => m.label).join(', ')}` : ''}
          >
            {busy ? 'Generating…' : 'Generate .docx'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: { padding: '0.5rem 0' },
  loading: { padding: '2rem', color: '#6b7585', fontSize: 13 },
  intro: { fontSize: 13, color: '#6b7585', lineHeight: 1.55, marginBottom: 16 },
  error: {
    background: '#fee2e2', color: '#991b1b', padding: '10px 14px',
    borderRadius: 6, marginBottom: 16, fontSize: 13,
  },
  grid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 12,
  },
  card: {
    background: 'white', border: '1px solid #e5e7eb',
    borderRadius: 12, padding: '14px 16px',
    display: 'flex', flexDirection: 'column',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  cardIcon: { fontSize: 18, fontWeight: 700, width: 20, textAlign: 'center' },
  cardTitle: { fontWeight: 600, fontSize: 14, color: '#171D29' },
  cardDesc: {
    fontSize: 12, color: '#6b7585', lineHeight: 1.5,
    marginBottom: 12, flex: 1, minHeight: 48,
  },
  cardBtn: {
    width: '100%', padding: '7px 12px', border: '1px solid #d1d5db',
    background: 'white', color: '#171D29', borderRadius: 6,
    fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  cardBtnDisabled: {
    width: '100%', padding: '7px 12px', border: '1px dashed #d1d5db',
    background: 'transparent', color: '#9ca3af', borderRadius: 6,
    fontSize: 13, cursor: 'default', fontFamily: 'inherit',
  },
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(560px, 94vw)', maxHeight: '86vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 18, color: '#171D29', marginBottom: 4,
  },
  modalSubtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 18 },
  label: {
    display: 'block', fontSize: 10, color: '#6b7585',
    textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit', resize: 'vertical',
  },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  cancelBtn: {
    padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  primaryBtn: {
    padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default LettersTab;
